/**
 * Company API Module
 * All company-related API endpoints
 */

import { api } from './client.js';

/**
 * Company API endpoints
 */
export const companyApi = {
  /**
   * Get all companies with optional filters and pagination
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10, max: 100)
   * @param {string} params.status - Filter by status (Active, Trial, Inactive)
   * @param {string} params.search - Search term for name, email, or jobscribe_id
   * @returns {Promise<Object>} Companies data with pagination
   */
  getAll: (params = {}) => {
    // Filter out undefined/null values
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});

    const queryParams = new URLSearchParams(cleanParams).toString();
    const endpoint = `/admin/companies${queryParams ? `?${queryParams}` : ''}`;
    return api.get(endpoint);
  },

  /**
   * Get company by ID
   * @param {string|number} id - Company ID
   * @returns {Promise<Object>} Company data
   */
  getById: (id) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.get(`/admin/companies/${id}`);
  },

  /**
   * Get company-specific trial & trial API configuration
   * @param {string|number} id - Company ID
   */
  getTrialConfig: (id) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.get(`/admin/companies/${id}/trial-config`);
  },

  /**
   * Update company-specific trial & trial API configuration
   * @param {string|number} id - Company ID
   * @param {Object} payload - override fields or { clear_overrides: true }
   */
  updateTrialConfig: (id, payload) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.put(`/admin/companies/${id}/trial-config`, payload);
  },

  /**
   * Get company insights dashboard (aggregated metrics)
   * @param {string|number} id - Company ID
   * @returns {Promise<Object>} Insights data
   */
  getInsights: (id) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.get(`/admin/companies/${id}/insights`);
  },

  /**
   * Paginated API catalog import history for a company
   * @param {string|number} id - Company ID
   * @param {Object} params - page, limit, search, period
   */
  getApiImportHistory: (id, params = {}) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/companies/${id}/api-import-history${query ? `?${query}` : ''}`);
  },

  /**
   * Paginated job listings for admin insights (manual or API)
   * @param {string|number} id - Company ID
   * @param {Object} params - listingType (manual|api), page, limit, search, period, status
   */
  getJobListings: (id, params = {}) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/companies/${id}/job-listings${query ? `?${query}` : ''}`);
  },

  /**
   * Paginated operational activity feed (jobs, candidates, tickets)
   * @param {string|number} id - Company ID
   * @param {Object} params - page, limit, search, period, category
   */
  getActivityFeed: (id, params = {}) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/companies/${id}/activity-feed${query ? `?${query}` : ''}`);
  },

  /**
   * Paginated subscription audit log
   * @param {string|number} id - Company ID
   * @param {Object} params - page, limit, search, period
   */
  getSubscriptionAudit: (id, params = {}) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    const cleanParams = Object.entries(params).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        acc[key] = value;
      }
      return acc;
    }, {});
    const query = new URLSearchParams(cleanParams).toString();
    return api.get(`/admin/companies/${id}/subscription-audit${query ? `?${query}` : ''}`);
  },

  /**
   * Get company by JobScribe ID
   * @param {string} jobscribeId - JobScribe ID (e.g., JS-COMP-0001)
   * @returns {Promise<Object>} Company data
   */
  getByJobscribeId: (jobscribeId) => {
    if (!jobscribeId) {
      return Promise.reject(new Error('JobScribe ID is required'));
    }
    return api.get(`/admin/companies/jobscribe/${jobscribeId}`);
  },

  /**
   * Create new company
   * @param {Object} companyData - Company data
   * @param {string} companyData.name - Company name (required)
   * @param {string} [companyData.email] - Contact email
   * @param {string} [companyData.phone] - Phone number
   * @param {string} [companyData.website] - Website URL
   * @param {string} [companyData.address] - Address
   * @param {string} [companyData.status] - Status (Active, Trial, Inactive)
   * @param {string} [companyData.plan] - Plan (Basic, Pro, Enterprise)
   * @param {number} [companyData.users_count] - Number of users
   * @param {number} [companyData.api_limit] - API limit
   * @param {number} [companyData.api_used] - API usage count
   * @returns {Promise<Object>} Created company data
   */
  create: (companyData) => {
    if (!companyData || !companyData.name) {
      return Promise.reject(new Error('Company name is required'));
    }
    return api.post('/admin/companies', companyData);
  },

  /**
   * Update company
   * @param {string|number} id - Company ID
   * @param {Object} companyData - Updated company data (all fields optional)
   * @returns {Promise<Object>} Updated company data
   */
  update: (id, companyData) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    if (!companyData || Object.keys(companyData).length === 0) {
      return Promise.reject(new Error('Company data is required'));
    }
    return api.put(`/admin/companies/${id}`, companyData);
  },

  /**
   * Record manual offline payment and activate/update subscription
   * @param {string|number} id - Company ID
   * @param {Object} payload - Manual subscription payload
   * @returns {Promise<Object>} Updated company, transaction, and event data
   */
  recordManualSubscription: (id, payload) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.post(`/admin/companies/${id}/manual-subscription`, payload);
  },

  /**
   * Delete company (soft delete)
   * @param {string|number} id - Company ID
   * @returns {Promise<Object>} Deletion confirmation
   */
  delete: (id) => {
    if (!id) {
      return Promise.reject(new Error('Company ID is required'));
    }
    return api.delete(`/admin/companies/${id}`);
  },

  /**
   * Get company statistics overview
   * @returns {Promise<Object>} Company statistics
   */
  getStats: () => {
    return api.get('/admin/companies/stats/overview');
  },

  /**
   * Company login
   * @param {Object} credentials - Login credentials
   * @param {string} credentials.email - Company email
   * @param {string} credentials.password - Company password
   * @returns {Promise<Object>} Login response with token and company data
   */
  login: (credentials) => {
    if (!credentials || !credentials.email || !credentials.password) {
      return Promise.reject(new Error('Email and password are required'));
    }
    return api.post('/admin/companies/login', credentials);
  },
};

export default companyApi;
